import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NotificationWizardResource } from '../notification-wizard-type';
import moment from 'moment/moment';
import { MatDialogRef } from '@angular/material/dialog';
import { NotificationWizardComponent } from '../notification-wizard-dialog.component';
import { CommunicationsService } from '../../../communications/communications.service';
import { CommunicationService } from '../../../api-users/api/communication.service';
import { FuseConfirmationService } from 'fuseui';

@Component({
    selector: 'app-notification-wizard-summary',
    templateUrl: './notification-wizard-summary.component.html',
    styleUrls: ['./notification-wizard-summary.styles.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class NotificationWizardSummaryComponent implements OnInit {
    @Output() saveEmitter: EventEmitter<string> = new EventEmitter<string>();
    protected readonly moment = moment;

    notificationWizard: NotificationWizardResource;

    constructor(
        private toastr: ToastrService,
        private _changeDetectorRef: ChangeDetectorRef,
        private _communicationService: CommunicationService,
        public dialogRef: MatDialogRef<NotificationWizardComponent>,
        private _comService: CommunicationsService,
        private translateService: TranslateService,
        private _fuseConfirmationService: FuseConfirmationService,
    ) {
        if (sessionStorage.getItem('notificationWizard')) {
            // @ts-ignore
            this.notificationWizard = JSON.parse(sessionStorage.getItem('notificationWizard')) as NotificationWizard;
        } else {
            this.notificationWizard = {};
        }
    }

    async ngOnInit(): Promise<void> {
        this._changeDetectorRef.markForCheck();
        // userCount is already calculated and saved from the filter step
    }

    getSelectedCompanyName(): string {
        if (!this.notificationWizard.companyId || !this.notificationWizard.companyList) {
            return '';
        }
        const company = this.notificationWizard.companyList.find((c: any) => c.id === this.notificationWizard.companyId);
        return company ? company.name : '';
    }


    sendNowChanged(event: any) {
        if (event) {
            this.notificationWizard.sendTime = moment().format('YYYY-MM-DD HH:mm');
            this._changeDetectorRef.markForCheck();
        }
    }

    save() {
        if (this.notificationWizard.sendTime === undefined) { return; }

        const userIds: number[] = this.notificationWizard.userIds || [];
        const groupIds: number[] = this.notificationWizard.groupIds || [];
        const companyId: number | null = this.notificationWizard.companyId || null;

        if (userIds.length === 0 && groupIds.length === 0) {
            const companyName = this.getSelectedCompanyName();
            const message = companyName
                ? this.translateService.instant('section.news.noRecipients.messageWithCompany', { companyName })
                : this.translateService.instant('section.news.noRecipients.message');
            const dialogRef = this._fuseConfirmationService.open({
                title: this.translateService.instant('section.news.noRecipients.title'),
                message,
                icon: { show: true, name: 'heroicons_outline:exclamation-triangle', color: 'warning' },
                actions: {
                    confirm: { show: true, label: this.translateService.instant('section.news.noRecipients.confirm'), color: 'primary' },
                    cancel: { show: true, label: this.translateService.instant('section.news.noRecipients.cancel') }
                },
                dismissible: true
            });
            dialogRef.afterClosed().subscribe(result => {
                if (result === 'confirmed') { this._doSave(userIds, groupIds, companyId); }
            });
            return;
        }

        this._doSave(userIds, groupIds, companyId);
    }

    private _doSave(userIds: number[], groupIds: number[], companyId: number | null): void {
        const req: any = {
            sendAt: this.notificationWizard.sendNow
                ? moment().format('YYYY-MM-DD HH:mm').toString()
                : moment(this.notificationWizard.sendTime).format('YYYY-MM-DD HH:mm').toString(),
            userIds: userIds.length > 0 ? userIds : null,
            groupIds: groupIds.length > 0 ? groupIds : null,
            companyId: companyId,
            subject: this.notificationWizard.subject ? this.notificationWizard.subject : '',
            iosMessage: this.notificationWizard.iosMessage,
            androidMessage: this.notificationWizard?.androidMessage,
        };

        if(this.notificationWizard.attachmentType) {
            req.attachmentType = this.notificationWizard.attachmentType?.type;
            if (this.notificationWizard.attachmentType?.type === 'rich_content') {
                req.richContent = this.notificationWizard.richContent;
                if (this.notificationWizard.link) {
                    req.link = this.notificationWizard.link;
                }
            } else {
                req.attachment = this.notificationWizard.attachment?.toString();
            }
        }

        if (this.notificationWizard.id) {
            this._comService.editCommunication(+this.notificationWizard.id, req).subscribe(value => {
                this._comService.getCommunications().subscribe();
                this.toastr.success(this.translateService.instant('section.seller.data_edited'));
                this._changeDetectorRef.markForCheck();
                sessionStorage.removeItem('notificationWizard');
                this.dialogRef.close(value);
            });
        } else {
            this._communicationService.createNewCommunication(req).subscribe(
                () => {
                    sessionStorage.setItem('notificationWizard', JSON.stringify(this.notificationWizard));
                    this.saveEmitter.emit('');
                    this.toastr.success(this.translateService.instant('section.notification-wizard.success'));
                    this._changeDetectorRef.markForCheck();
                    sessionStorage.removeItem('notificationWizard');
                    this.dialogRef.close();
                },
                (error: any) => {
                    this.toastr.error(this.translateService.instant(error?.error?.message));
                    this._changeDetectorRef.markForCheck();
                },
            );
        }
    }

    clearDate(event: any) {
        event.stopPropagation();
        this.notificationWizard.sendTime = undefined;
        this._changeDetectorRef.markForCheck();
    }
}
